// frontend/src/components/Approval/GiphyPanel.jsx
import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { GiphyFetch } from '@giphy/js-fetch-api';
import { Grid } from '@giphy/react-components';
import { Search, X } from 'lucide-react';
import debounce from 'lodash/debounce';
import './GiphyPanel.css';

const gf = new GiphyFetch(import.meta.env.VITE_GIPHY_API_KEY);

export default function GiphyPanel({ onSelectGif, onClose }) {
  const { t } = useTranslation();
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const debouncedSetSearch = useMemo(
    () => debounce((value) => setSearchTerm(value), 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [debouncedSetSearch]);

  const fetchGifs = async (offset) => {
    try {
      setIsLoading(true);
      setError(null);
      if (searchTerm) {
        return await gf.search(searchTerm, { offset, limit: 10, type: 'stickers' });
      }
      return await gf.trending({ offset, limit: 10, type: 'stickers' });
    } catch (err) {
      console.error('Giphy API error:', err);
      setError(t('upload:gif.apiError') || 'Failed to load GIFs. Please try again.');
      return { data: [] };
    } finally {
      setIsLoading(false);
    }
  };

  const handleGifClick = (gif, e) => {
    e.preventDefault();
    onSelectGif({
      giphy_id: gif.id,
      giphy_url: gif.images.original.url,
      position: { x: 540, y: 960 }, // Center of 1080x1920
      size: { width: 200, height: 200 },
      opacity: 1.0,
      layer_order: 1
    });
  };

  return (
    <div className="giphy-panel">
      <div className="giphy-panel-header">
        <div className="giphy-search-bar">
          <Search className="search-icon" size={20} />
          <input
            type="text"
            placeholder={t('upload:gif.searchPlaceholder')}
            value={localSearchTerm}
            onChange={(e) => {
              setLocalSearchTerm(e.target.value);
              debouncedSetSearch(e.target.value);
            }}
            className="giphy-search-input"
          />
        </div>
        <button onClick={onClose} className="giphy-close-btn">
          <X size={24} />
        </button>
      </div>

      <div className="giphy-grid-container">
        {error && (
          <div className="giphy-error">
            <p>{error}</p>
          </div>
        )}

        {isLoading && (
          <div className="giphy-loading">
            <p>Loading GIFs...</p>
          </div>
        )}

        <Grid
          key={searchTerm}
          width={380}
          columns={2}
          fetchGifs={fetchGifs}
          onGifClick={handleGifClick}
        />
      </div>
    </div>
  );
}

GiphyPanel.propTypes = {
  onSelectGif: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};
